import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import ConfirmModal from './ConfirmModal';
import ApiErrorModal from './ApiErrorModal';
import CountrySelect from './CountrySelect';
import { detectUserLocationData } from '../utils/geo';

export default function AddressManager({ userId, shouldFetch = true, onLoaded }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isAdding, setIsAdding] = useState(false);
  
  const handleAddClick = async () => {
    setIsAdding(true);
    setFormError(null);
    const loc = await detectUserLocationData();
    if (loc?.iso2) {
      setNewAddr(prev => ({ ...prev, countryCode: loc.iso2 }));
    }
  };
  const [editingId, setEditingId] = useState(null);
  
  const initialAddressState = {
    countryCode: 'US',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvinceRegion: '',
    postalCode: '',
    usages: [],
    isPrimary: false
  };
  
  const [newAddr, setNewAddr] = useState(initialAddressState);
  const [editAddrVal, setEditAddrVal] = useState(initialAddressState);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: "", message: "" });
  const [formError, setFormError] = useState(null);
  
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const isBusy = saving || isAdding || editingId !== null;
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (shouldFetch) {
      fetchAddresses();
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [shouldFetch, userId]);

  const fetchAddresses = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setLoading(true);
    try {
      const res = await api.get('/users/me/addresses', { signal: abortControllerRef.current.signal });
      setAddresses(res.data?.addresses || []);
      setError(null);
      if (onLoaded) onLoaded();
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      setError('Failed to load addresses.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newAddr.addressLine1.trim() || !newAddr.city.trim() || !newAddr.countryCode.trim()) {
      setFormError('Country, Address Line 1, and City are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...newAddr, isPrimary: newAddr.isPrimary || addresses.length === 0 };
      const res = await api.post('/users/me/addresses', payload);
      if (res.data?.address) {
        const added = res.data.address;
        if (added.isPrimary) {
          setAddresses(prev => [...prev.map(a => ({ ...a, isPrimary: false })), added]);
        } else {
          setAddresses(prev => [...prev, added]);
        }
      } else {
        await fetchAddresses();
      }
      setIsAdding(false);
      setNewAddr(initialAddressState);
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to add address' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editAddrVal.addressLine1.trim() || !editAddrVal.city.trim() || !editAddrVal.countryCode.trim()) {
      setFormError('Country, Address Line 1, and City are required.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/users/me/addresses/${id}`, editAddrVal);
      if (res.data?.address) {
        const updated = res.data.address;
        if (updated.isPrimary) {
          setAddresses(prev => prev.map(a => a.id === id ? updated : { ...a, isPrimary: false }));
        } else {
          setAddresses(prev => prev.map(a => a.id === id ? updated : a));
        }
      }
      setFormError(null); setEditingId(null);
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to update address' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    setSaving(true);
    try {
      await api.delete(`/users/me/addresses/${rowToDelete}`);
      await fetchAddresses();
      setShowConfirm(false);
      setRowToDelete(null);
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to delete address' });
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (id) => {
    setSaving(true);
    try {
      await api.put(`/users/me/addresses/${id}/primary`);
      setAddresses(prev => prev.map(a => ({ ...a, isPrimary: a.id === id })));
    } catch (err) {
      setErrorModal({ isOpen: true, title: "Error", message: err.response?.data?.message || 'Failed to set primary address' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && addresses.length === 0) {
    return <div className="text-sm text-slate-500 animate-pulse">Loading addresses...</div>;
  }

  const renderForm = (val, setVal, onSave, onCancel, saveText) => (
    <div className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-700 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="md:col-span-2 lg:col-span-3">
          <label className="block text-xs text-slate-700 dark:text-slate-300 mb-1">Country</label>
          <div className="w-64">
            <CountrySelect value={val.countryCode} onChange={(c) => setVal({...val, countryCode: c.iso2})} preload={true} />
          </div>
        </div>

        <div className="md:col-span-2 lg:col-span-3">
          <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1 font-medium">Address Line 1</label>
          <input value={val.addressLine1} onChange={(ev) => setVal({...val, addressLine1: ev.target.value})} placeholder="Street address" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>

        <div className="md:col-span-2 lg:col-span-3">
          <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1 font-medium">Address Line 2</label>
          <input value={val.addressLine2} onChange={(ev) => setVal({...val, addressLine2: ev.target.value})} placeholder="Apt, suite, unit, building (optional)" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>

        <div>
          <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1 font-medium">City</label>
          <input value={val.city} onChange={(ev) => setVal({...val, city: ev.target.value})} placeholder="City" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>

        <div>
          <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1 font-medium">{val.countryCode === 'US' ? 'State' : val.countryCode === 'UK' ? 'County' : 'State / Province'}</label>
          <input value={val.stateProvinceRegion} onChange={(ev) => setVal({...val, stateProvinceRegion: ev.target.value})} placeholder={val.countryCode === 'US' ? 'e.g., CA' : val.countryCode === 'UK' ? 'e.g., Greater London' : 'State / Province'} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>

        <div>
          <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1 font-medium">{val.countryCode === 'US' ? 'ZIP Code' : val.countryCode === 'UK' ? 'Postcode' : 'Postal Code'}</label>
          <input value={val.postalCode} onChange={(ev) => setVal({...val, postalCode: ev.target.value})} placeholder={val.countryCode === 'US' ? 'ZIP code' : val.countryCode === 'UK' ? 'Postcode' : 'Postal code'} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>

        <div className="md:col-span-2 lg:col-span-3 flex flex-wrap gap-4 mt-1">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={val.usages.includes('shipping')} onChange={(ev) => {
              const nextUsages = [...val.usages];
              if (ev.target.checked) { if (!nextUsages.includes('shipping')) nextUsages.push('shipping'); } 
              else { const i = nextUsages.indexOf('shipping'); if (i > -1) nextUsages.splice(i, 1); }
              setVal({...val, usages: nextUsages});
            }} className="h-4 w-4 text-indigo-500" />
            Use for Shipping Address
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={val.usages.includes('billing')} onChange={(ev) => {
              const nextUsages = [...val.usages];
              if (ev.target.checked) { if (!nextUsages.includes('billing')) nextUsages.push('billing'); } 
              else { const i = nextUsages.indexOf('billing'); if (i > -1) nextUsages.splice(i, 1); }
              setVal({...val, usages: nextUsages});
            }} className="h-4 w-4 text-indigo-500" />
            Use for Billing Address
          </label>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end mt-4">
        <button type="button" onClick={onCancel} disabled={saving} className="px-3 py-1.5 text-xs text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 transition">Cancel</button>
        <button type="button" onClick={onSave} disabled={saving} className="px-3 py-1.5 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 transition shadow-sm">{saving ? 'Saving...' : saveText}</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Addresses</h3>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAddresses} disabled={isBusy || loading} className={`p-1 text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-50 ${isBusy ? 'hidden' : ''}`}>
             <svg className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
          {!isBusy && (
            <button type="button" onClick={handleAddClick} className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50">
              + Add Address
            </button>
          )}
        </div>
      </div>
      
      {error && <div className="text-xs text-red-600">{error}</div>}

      <div className="space-y-3">
        {addresses.map(a => {
          if (editingId === a.id) {
            return (
              <React.Fragment key={a.id}>
                {renderForm(editAddrVal, setEditAddrVal, () => handleUpdate(a.id), () => { setFormError(null); setEditingId(null); }, 'Save')}
              </React.Fragment>
            );
          }

          return (
            <div key={a.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="flex items-start gap-3">
                <label className={`cursor-pointer flex items-center pt-1 ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                  <input type="radio" name="primaryAddressRadio" checked={a.isPrimary || a.is_primary} onChange={() => handleSetPrimary(a.id)} disabled={isBusy} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-50" />
                </label>
                <div className="space-y-1 text-sm text-slate-800 dark:text-slate-200">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold">{a.addressLine1} {a.addressLine2}</span>
                    {(a.isPrimary || a.is_primary) && <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">Primary</span>}
                  </div>
                  <p>{a.city}{a.stateProvinceRegion ? `, ${a.stateProvinceRegion}` : ''} {a.postalCode}</p>
                  <p>{a.countryCode || a.country}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Usage: {(Array.isArray(a.usages) && a.usages.length > 0) ? a.usages.join(', ') : '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 sm:mt-0">
                <button type="button" onClick={() => { setEditAddrVal(a); setFormError(null); setEditingId(a.id); }} disabled={isBusy} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition-colors disabled:opacity-40" title="Edit address">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                </button>
                <button type="button" onClick={() => { setRowToDelete(a.id); setShowConfirm(true); }} disabled={isBusy} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-40" title="Delete address">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                </button>
              </div>
            </div>
          );
        })}

        {addresses.length === 0 && !isAdding && (
          <p className="text-sm text-slate-500 italic p-4 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">No addresses added yet.</p>
        )}
      </div>

      {isAdding && renderForm(newAddr, setNewAddr, handleAdd, () => setIsAdding(false), 'Add Address')}

      <ConfirmModal
        isOpen={showConfirm}
        title="Delete Address"
        message="Are you sure you want to delete this address? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowConfirm(false)}
        isDestructive={true}
      />
      <ApiErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onCancel={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
        onReload={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
